import React, { useMemo } from 'react'

import {
  DefaultNodeTypes,
  SerializedBlockNode,
  SerializedLinkNode,
} from '@payloadcms/richtext-lexical'
import {
  RichText as ConvertRichText,
  JSXConvertersFunction,
  LinkJSXConverter,
} from '@payloadcms/richtext-lexical/react'

import { BannerBlock } from '@/blocks/Banner/Component'
import { CallToActionBlock } from '@/blocks/CallToAction/Component'
import { CodeBlock, CodeBlockProps } from '@/blocks/Code/Component'
import { ImportantTextBlock, ImportantTextBlockProps } from '@/blocks/ImportantText/Component'
import { MediaBlock } from '@/blocks/MediaBlock/Component'
import { TableBlock } from '@/blocks/Table/Component'
import { cn } from '@/utilities/ui'

import type {
  BannerBlock as BannerBlockProps,
  CallToActionBlock as CTABlockProps,
  MediaBlock as MediaBlockProps,
  TableBlock as TableBlockProps,
} from '@/payload-types'
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'

type SerializedImageNode = {
  type: 'image'
  version: number
  src?: string
  alt?: string
  mediaId?: number | null
}

type SerializedVideoEmbedNode = {
  type: 'videoEmbed'
  version: number
  url?: string
  provider?: string
  embedUrl?: string
}

type NodeTypes =
  | DefaultNodeTypes
  | SerializedImageNode
  | SerializedVideoEmbedNode
  | SerializedBlockNode<
      | CTABlockProps
      | MediaBlockProps
      | BannerBlockProps
      | CodeBlockProps
      | TableBlockProps
      | ImportantTextBlockProps
    >

const internalDocToHref = ({ linkNode }: { linkNode: SerializedLinkNode }) => {
  const { value, relationTo } = linkNode.fields.doc!
  if (typeof value !== 'object') {
    throw new Error('Expected value to be an object')
  }
  const slug = value.slug
  return relationTo === 'articles' ? `/learn/articles/${slug}` : `/${slug}`
}

/** External/custom URLs always open in a new tab; internal links respect the editor's newTab setting. */
const shouldOpenInNewTab = (fields: SerializedLinkNode['fields']) =>
  fields.linkType === 'custom' || fields.newTab

const resolveLinkHref = (node: SerializedLinkNode): string => {
  if (node.fields.linkType === 'internal') {
    try {
      return internalDocToHref({ linkNode: node })
    } catch {
      console.error(
        'Lexical => JSX converter: Link converter: found internal link, but internalDocToHref failed',
      )
      return '#'
    }
  }
  return node.fields.url ?? ''
}

const _defaultLinkConverters = LinkJSXConverter({ internalDocToHref })

const articleLinkConverters: Pick<typeof _defaultLinkConverters, 'autolink' | 'link'> = {
  autolink: ({ node, nodesToJSX }) => {
    const children = nodesToJSX({ nodes: node.children })
    return (
      <a href={node.fields.url} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    )
  },
  link: ({ node, nodesToJSX }) => {
    const children = nodesToJSX({ nodes: node.children })
    const openInNewTab = shouldOpenInNewTab(node.fields)
    return (
      <a
        href={resolveLinkHref(node)}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
      >
        {children}
      </a>
    )
  },
}

const baseJsxConverters: JSXConvertersFunction<NodeTypes> = ({ defaultConverters }) => ({
  ...defaultConverters,
  ...articleLinkConverters,
  // Custom nodes from EventsForm RichTextArea (and any leftover image nodes).
  image: ({ node }) => {
    const src = typeof node.src === 'string' ? node.src : ''
    const alt = typeof node.alt === 'string' ? node.alt : ''
    if (!src) return null
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img src={src} alt={alt} className="my-4 max-h-128 w-auto max-w-full rounded-lg" />
    )
  },
  videoEmbed: ({ node }) => {
    const embedUrl = typeof node.embedUrl === 'string' ? node.embedUrl : ''
    const provider = typeof node.provider === 'string' ? node.provider : 'video'
    if (!embedUrl) return null
    return (
      <div className="my-4 aspect-video w-full overflow-hidden rounded-lg border border-border">
        <iframe
          src={embedUrl}
          title={`${provider} video`}
          className="size-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    )
  },
  blocks: {
    banner: ({ node }) => <BannerBlock className="col-start-2 mb-4" {...node.fields} />,
    mediaBlock: ({ node }) => (
      <MediaBlock
        className="col-span-3 col-start-1"
        imgClassName="m-0"
        {...node.fields}
        captionClassName="mx-auto max-w-[48rem]"
        enableGutter={false}
        disableInnerContainer={true}
      />
    ),
    code: ({ node }) => <CodeBlock className="col-start-2" {...node.fields} />,
    cta: ({ node }) => <CallToActionBlock {...node.fields} />,
    table: ({ node }) => <TableBlock {...node.fields} />,
    importantText: ({ node }) => <ImportantTextBlock {...node.fields} />,
  },
})

type Props = {
  data: DefaultTypedEditorState
  /** When provided, headings are rendered with these IDs in order (for table of contents / anchor links). */
  headingIds?: string[]
  enableGutter?: boolean
  enableProse?: boolean
  // If you add this value you need to specify all font styles prose handles
  fontOverride?: string
} & React.HTMLAttributes<HTMLDivElement>

export default function RichText(props: Props) {
  const {
    className,
    data,
    headingIds,
    enableProse = true,
    enableGutter = true,
    fontOverride = 'prose',
    ...rest
  } = props

  const converters = useMemo((): JSXConvertersFunction<NodeTypes> => {
    if (!headingIds?.length) return baseJsxConverters
    return (params) => {
      const base = baseJsxConverters(params)
      let h1Index = 0
      return {
        ...base,
        heading: (args) => {
          const { node, nodesToJSX } = args
          const children = nodesToJSX({ nodes: node.children })
          const NodeTag = node.tag as 'h1' | 'h2' | 'h3'
          if (node.tag !== 'h1') {
            return React.createElement(NodeTag, null, children)
          }
          const id = headingIds[h1Index++] ?? ''
          return React.createElement(NodeTag, { id, className: 'scroll-mt-20' }, children)
        },
      }
    }
  }, [headingIds])

  return (
    <ConvertRichText
      converters={converters}
      data={data}
      className={cn(
        'payload-richtext',
        fontOverride ? fontOverride : 'prose',
        {
          'max-w-none': !enableGutter,
          'md:prose-md dark:prose-invert mx-auto text-primary': enableProse,
        },
        className,
      )}
      {...rest}
    />
  )
}
